import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Button, Spin } from 'antd';
import {
    UserOutlined,
    UserAddOutlined,
    FileTextOutlined,
    HomeOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import * as echarts from 'echarts';
import { getDashboardStatistic } from 'api/dashboard';
import './index.less';

const Dashboard = () => {
    // 数据状态
    const [loading, setLoading] = useState(false);
    const [statsData, setStatsData] = useState({
        totalPeople: 0, // 在院总人数
        todayCheckIn: 0, // 今日入院
        pendingCount: 0, // 待办待审人数
        bedUsageRate: 0, // 床位使用率
    });

    const [todoList, setTodoList] = useState([]);
    const [nursingAreas, setNursingAreas] = useState([]);
    const [bedUsage, setBedUsage] = useState({
        total: 0,
        occupied: 0,
        available: 0,
        usageRate: 0,
    });
    const [trendDays, setTrendDays] = useState(7);
    const [selectedNursingArea, setSelectedNursingArea] = useState(null);
    const [trendData, setTrendData] = useState({ dates: [], checkInData: [], checkOutData: [] });
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const pieChartRef = useRef(null);
    const pieChartInstance = useRef(null);

    // 获取所有仪表盘数据
    const fetchDashboardData = async (days = 7) => {
        setLoading(true);
        try {
            const res = await getDashboardStatistic(days);
            if (res) {
                const {
                    totalCount,
                    applicationRecordStatistic,
                    checkInRecordStatistic,
                    nursebedStatistic,
                } = res;

                // 设置统计卡片数据
                const totalBeds = nursebedStatistic.totalBeds || 0;
                const remainingBeds = nursebedStatistic.remainingBeds || 0;
                const occupiedBeds = totalBeds - remainingBeds;
                const usageRate = totalBeds > 0 ? ((occupiedBeds / totalBeds) * 100).toFixed(1) : 0;

                // 获取今日入院人数（checkInRecordStatistic 数组的第一个元素）
                const todayCheckIn =
                    checkInRecordStatistic && checkInRecordStatistic.length > 0
                        ? checkInRecordStatistic[0].admissionCount
                        : 0;

                setStatsData({
                    totalPeople: totalCount || 0,
                    todayCheckIn,
                    pendingCount: applicationRecordStatistic?.totalCount || 0,
                    bedUsageRate: parseFloat(usageRate),
                });

                // 设置待办事项列表
                if (applicationRecordStatistic) {
                    setTodoList([
                        { label: '复核', count: applicationRecordStatistic.reviewCount || 0 },
                        {
                            label: '轮候申请',
                            count: applicationRecordStatistic.waitForApproveCount || 0,
                        },
                        {
                            label: '院前评估',
                            count: applicationRecordStatistic.preHospitalAssessmentCount || 0,
                        },
                    ]);
                }

                // 设置护理区数据
                if (nursebedStatistic) {
                    const {
                        takeCareSelfTotal,
                        takeCareSelfRemaining,
                        semiCareTotal,
                        semiCareRemaining,
                        fullCareTotal,
                        fullCareRemaining,
                    } = nursebedStatistic;

                    const selfOccupied = takeCareSelfTotal - takeCareSelfRemaining;
                    const semiOccupied = semiCareTotal - semiCareRemaining;
                    const fullOccupied = fullCareTotal - fullCareRemaining;

                    setNursingAreas([
                        {
                            name: '自理区',
                            shortName: '自',
                            count: selfOccupied,
                            percent:
                                takeCareSelfTotal > 0
                                    ? ((selfOccupied / takeCareSelfTotal) * 100).toFixed(1)
                                    : 0,
                            color: '#1890ff',
                            iconClass: 'blue',
                            checkInCount: selfOccupied,
                            bedData: {
                                total: takeCareSelfTotal,
                                occupied: selfOccupied,
                                available: takeCareSelfRemaining,
                                usageRate:
                                    takeCareSelfTotal > 0
                                        ? ((selfOccupied / takeCareSelfTotal) * 100).toFixed(1)
                                        : 0,
                            },
                        },
                        {
                            name: '半护理区',
                            shortName: '半',
                            count: semiOccupied,
                            percent:
                                semiCareTotal > 0
                                    ? ((semiOccupied / semiCareTotal) * 100).toFixed(1)
                                    : 0,
                            color: '#52c41a',
                            iconClass: 'green',
                            checkInCount: semiOccupied,
                            bedData: {
                                total: semiCareTotal,
                                occupied: semiOccupied,
                                available: semiCareRemaining,
                                usageRate:
                                    semiCareTotal > 0
                                        ? ((semiOccupied / semiCareTotal) * 100).toFixed(1)
                                        : 0,
                            },
                        },
                        {
                            name: '全护理区',
                            shortName: '全',
                            count: fullOccupied,
                            percent:
                                fullCareTotal > 0
                                    ? ((fullOccupied / fullCareTotal) * 100).toFixed(1)
                                    : 0,
                            color: '#faad14',
                            iconClass: 'orange',
                            checkInCount: fullOccupied,
                            bedData: {
                                total: fullCareTotal,
                                occupied: fullOccupied,
                                available: fullCareRemaining,
                                usageRate:
                                    fullCareTotal > 0
                                        ? ((fullOccupied / fullCareTotal) * 100).toFixed(1)
                                        : 0,
                            },
                        },
                    ]);
                }

                // 设置床位使用情况（总计）
                setBedUsage({
                    total: totalBeds,
                    occupied: occupiedBeds,
                    available: remainingBeds,
                    usageRate: parseFloat(usageRate),
                });

                // 设置趋势数据
                if (checkInRecordStatistic && checkInRecordStatistic.length > 0) {
                    const dates = checkInRecordStatistic.map(item => {
                        const date = new Date(item.checkInDate);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                    });
                    const checkInData = checkInRecordStatistic.map(
                        item => item.admissionCount || 0,
                    );
                    const checkOutData = checkInRecordStatistic.map(
                        item => item.dischargeCount || 0,
                    );

                    setTrendData({ dates, checkInData, checkOutData });
                } else {
                    // 如果没有数据，清空趋势数据
                    setTrendData({ dates: [], checkInData: [], checkOutData: [] });
                }
            } else {
                // 如果 API 返回空，清空趋势数据
                setTrendData({ dates: [], checkInData: [], checkOutData: [] });
            }
        } catch (error) {
            console.error('获取仪表盘数据失败:', error);
            // 出错时不清空数据，保持原有数据显示
        } finally {
            setLoading(false);
        }
    };

    // 初始化数据
    useEffect(() => {
        fetchDashboardData(trendDays);
    }, []);

    // 初始化和更新 ECharts 图表
    useEffect(() => {
        if (!chartRef.current) return;

        // 初始化图表实例
        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        // 如果有数据，渲染图表
        if (trendData.dates.length > 0) {
            // 先清空图表
            chartInstance.current.clear();

            // 配置图表选项
            const option = {
                tooltip: {
                    trigger: 'axis',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderColor: '#e0e0e0',
                    borderWidth: 1,
                    textStyle: {
                        color: '#333',
                        fontSize: 13,
                    },
                    padding: [12, 16],
                    axisPointer: {
                        type: 'line',
                        lineStyle: {
                            color: '#1890ff',
                            width: 2,
                            type: 'solid',
                        },
                    },
                    formatter: function (params) {
                        let result = `<div style="font-weight: 600; margin-bottom: 8px; color: #262626;">${params[0].axisValue}</div>`;
                        params.forEach(item => {
                            result += `<div style="margin: 4px 0; display: flex; align-items: center;">
                                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${item.color}; margin-right: 8px;"></span>
                                <span style="color: #595959;">${item.seriesName}: </span>
                                <span style="font-weight: 600; color: #262626; margin-left: 4px;">${item.value}人</span>
                            </div>`;
                        });
                        return result;
                    },
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '12%',
                    top: '12%',
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: trendData.dates,
                    axisLine: {
                        lineStyle: {
                            color: '#e0e0e0',
                            width: 1,
                        },
                    },
                    axisTick: {
                        show: false,
                    },
                    axisLabel: {
                        color: '#666',
                        fontSize: 12,
                        interval: 'auto',
                        rotate: 0,
                        margin: 12,
                        formatter: function (value) {
                            return value;
                        },
                    },
                    boundaryGap: false,
                },
                yAxis: {
                    type: 'value',
                    minInterval: 1,
                    axisLine: {
                        show: false,
                    },
                    axisTick: {
                        show: false,
                    },
                    splitLine: {
                        lineStyle: {
                            color: '#f0f0f0',
                            type: 'dashed',
                            width: 1,
                        },
                    },
                    axisLabel: {
                        color: '#666',
                        fontSize: 12,
                        formatter: '{value}',
                    },
                },
                series: [
                    {
                        name: '入院人数',
                        type: 'line',
                        smooth: 0.3,
                        data: trendData.checkInData,
                        itemStyle: {
                            color: '#1890ff',
                            borderWidth: 2,
                            borderColor: '#fff',
                        },
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: 'rgba(24, 144, 255, 0.25)' },
                                { offset: 1, color: 'rgba(24, 144, 255, 0.02)' },
                            ]),
                        },
                        lineStyle: {
                            width: 3,
                            shadowColor: 'rgba(24, 144, 255, 0.3)',
                            shadowBlur: 10,
                            shadowOffsetY: 5,
                        },
                        symbol: 'circle',
                        symbolSize: 6,
                        showSymbol: trendData.dates.length <= 30,
                        emphasis: {
                            scale: true,
                            focus: 'series',
                            itemStyle: {
                                borderWidth: 3,
                                shadowBlur: 15,
                                shadowColor: 'rgba(24, 144, 255, 0.5)',
                            },
                        },
                    },
                    {
                        name: '出院人数',
                        type: 'line',
                        smooth: 0.3,
                        data: trendData.checkOutData,
                        itemStyle: {
                            color: '#52c41a',
                            borderWidth: 2,
                            borderColor: '#fff',
                        },
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: 'rgba(82, 196, 26, 0.25)' },
                                { offset: 1, color: 'rgba(82, 196, 26, 0.02)' },
                            ]),
                        },
                        lineStyle: {
                            width: 3,
                            shadowColor: 'rgba(82, 196, 26, 0.3)',
                            shadowBlur: 10,
                            shadowOffsetY: 5,
                        },
                        symbol: 'circle',
                        symbolSize: 6,
                        showSymbol: trendData.dates.length <= 30,
                        emphasis: {
                            scale: true,
                            focus: 'series',
                            itemStyle: {
                                borderWidth: 3,
                                shadowBlur: 15,
                                shadowColor: 'rgba(82, 196, 26, 0.5)',
                            },
                        },
                    },
                ],
            };

            // 设置图表选项，第二个参数 true 表示不合并，完全替换
            chartInstance.current.setOption(option, true);
        } else {
            // 如果没有数据，清空图表
            chartInstance.current.clear();
        }

        // 监听窗口大小变化，自适应图表
        const handleResize = () => {
            chartInstance.current?.resize();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [trendData, trendDays]);

    // 组件卸载时销毁图表实例
    useEffect(() => {
        return () => {
            chartInstance.current?.dispose();
            pieChartInstance.current?.dispose();
        };
    }, []);

    // 初始化和更新饼图
    useEffect(() => {
        if (pieChartRef.current && nursingAreas.length > 0) {
            // 初始化饼图实例
            if (!pieChartInstance.current) {
                pieChartInstance.current = echarts.init(pieChartRef.current);
            }

            // 准备饼图数据
            const pieData = nursingAreas.map(area => ({
                name: area.name,
                value: area.count,
                itemStyle: {
                    color: area.color,
                },
            }));

            // 配置饼图选项
            const option = {
                tooltip: {
                    trigger: 'item',
                    formatter: '{a} <br/>{b}: {c} ({d}%)',
                },
                series: [
                    {
                        name: '护理区分布',
                        type: 'pie',
                        radius: ['40%', '70%'],
                        center: ['50%', '50%'],
                        avoidLabelOverlap: false,
                        itemStyle: {
                            borderRadius: 10,
                            borderColor: '#fff',
                            borderWidth: 2,
                        },
                        label: {
                            show: false,
                        },
                        emphasis: {
                            label: {
                                show: true,
                                fontSize: 14,
                                fontWeight: 'bold',
                            },
                        },
                        labelLine: {
                            show: false,
                        },
                        data: pieData,
                    },
                ],
            };

            // 设置图表选项
            pieChartInstance.current.setOption(option);

            // 监听窗口大小变化，自适应图表
            const handleResize = () => {
                pieChartInstance.current?.resize();
            };
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [nursingAreas]);

    // 点击护理区时更新床位使用情况
    const handleNursingAreaClick = area => {
        setSelectedNursingArea(area.name);
        if (area.bedData) {
            setBedUsage(area.bedData);
        }
    };

    // 切换趋势天数
    const handleTrendDaysChange = days => {
        // 避免重复请求相同天数的数据
        if (days === trendDays) {
            return;
        }
        setTrendDays(days);
        fetchDashboardData(days);
    };

    return (
        <Spin spinning={loading}>
            <div className="dashboard-container">
                {/* 顶部统计卡片 */}
                <Row gutter={[16, 16]} className="stats-cards">
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="stat-card">
                            <div className="stat-content">
                                <div className="stat-info">
                                    <div className="stat-label">在院总人数</div>
                                    <div className="stat-value">{statsData.totalPeople}</div>
                                </div>
                                <div className="stat-icon blue">
                                    <UserOutlined />
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="stat-card">
                            <div className="stat-content">
                                <div className="stat-info">
                                    <div className="stat-label">今日入院</div>
                                    <div className="stat-value">{statsData.todayCheckIn}</div>
                                </div>
                                <div className="stat-icon green">
                                    <UserAddOutlined />
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="stat-card">
                            <div className="stat-content">
                                <div className="stat-info">
                                    <div className="stat-label">待办待审人数</div>
                                    <div className="stat-value">{statsData.pendingCount}</div>
                                </div>
                                <div className="stat-icon orange">
                                    <FileTextOutlined />
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="stat-card">
                            <div className="stat-content">
                                <div className="stat-info">
                                    <div className="stat-label">床位使用率</div>
                                    <div className="stat-value">{statsData.bedUsageRate}%</div>
                                </div>
                                <div className="stat-icon purple">
                                    <HomeOutlined />
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* 中间内容区域 */}
                <Row gutter={[16, 16]} className="content-row">
                    {/* 人口趋势分析 */}
                    <Col xs={24} lg={16}>
                        <Card title="人口趋势分析" className="chart-card">
                            <div className="chart-header">
                                <div className="chart-legend">
                                    <span className="legend-item">
                                        <span className="legend-dot blue"></span>
                                        入院人数
                                    </span>
                                    <span className="legend-item">
                                        <span className="legend-dot green"></span>
                                        出院人数
                                    </span>
                                </div>
                                <div className="chart-tabs">
                                    <Button
                                        type={trendDays === 7 ? 'primary' : 'default'}
                                        size="small"
                                        onClick={() => handleTrendDaysChange(7)}>
                                        7天
                                    </Button>
                                    <Button
                                        type={trendDays === 30 ? 'primary' : 'default'}
                                        size="small"
                                        onClick={() => handleTrendDaysChange(30)}>
                                        30天
                                    </Button>
                                    <Button
                                        type={trendDays === 90 ? 'primary' : 'default'}
                                        size="small"
                                        onClick={() => handleTrendDaysChange(90)}>
                                        90天
                                    </Button>
                                </div>
                            </div>
                            <div className="chart-container" style={{ position: 'relative' }}>
                                <div
                                    ref={chartRef}
                                    style={{ width: '100%', height: '380px' }}></div>
                                {trendData.dates.length === 0 && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '380px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#999',
                                            fontSize: '16px',
                                            backgroundColor: '#fff',
                                            zIndex: 10,
                                        }}>
                                        <InboxOutlined
                                            style={{
                                                fontSize: '48px',
                                                marginBottom: '16px',
                                                color: '#d9d9d9',
                                            }}
                                        />
                                        <div>暂无数据</div>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Col>

                    {/* 待办事项人数 */}
                    <Col xs={24} lg={8}>
                        <Card title="待办事项人数" className="todo-card">
                            <div className="todo-list">
                                {todoList.length > 0 ? (
                                    todoList.map((item, index) => (
                                        <div className="todo-item" key={index}>
                                            <span className="todo-label">{item.label}</span>
                                            <span
                                                className={`todo-value ${
                                                    item.count === 0 ? 'zero' : ''
                                                }`}>
                                                {item.count}
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <div
                                        style={{
                                            textAlign: 'center',
                                            color: '#999',
                                            padding: '20px 0',
                                        }}>
                                        暂无待办事项
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* 底部内容区域 */}
                <Row gutter={[16, 16]} className="bottom-row">
                    {/* 护理区分析 */}
                    <Col xs={24} lg={8}>
                        <Card title="护理区分析" className="nursing-card">
                            <div className="chart-placeholder">
                                <div
                                    ref={pieChartRef}
                                    style={{ width: '100%', height: '180px' }}></div>
                            </div>
                            <div className="nursing-legend">
                                {nursingAreas.map((area, index) => (
                                    <div className="legend-item" key={index}>
                                        <span
                                            className="legend-dot"
                                            style={{ backgroundColor: area.color }}></span>
                                        <span>
                                            {area.name} {area.count}人
                                        </span>
                                    </div>
                                ))}
                                {nursingAreas.length === 0 && (
                                    <div
                                        style={{
                                            textAlign: 'center',
                                            color: '#999',
                                            padding: '10px 0',
                                        }}>
                                        暂无数据
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Col>

                    {/* 护理区详情与床位使用 */}
                    <Col xs={24} lg={16}>
                        <Card title="护理区详情与床位使用" className="nursing-bed-card">
                            <Row gutter={[16, 16]}>
                                {/* 左侧：护理区列表 */}
                                <Col xs={24} md={12}>
                                    <div className="nursing-section">
                                        <div className="section-title">护理区列表</div>
                                        <div className="nursing-detail-list">
                                            {nursingAreas.length > 0 ? (
                                                nursingAreas.map((area, index) => (
                                                    <div
                                                        className={`nursing-detail-item ${
                                                            selectedNursingArea === area.name
                                                                ? 'active'
                                                                : ''
                                                        }`}
                                                        key={index}
                                                        onClick={() =>
                                                            handleNursingAreaClick(area)
                                                        }>
                                                        <div className="nursing-detail-header">
                                                            <span
                                                                className={`nursing-icon ${area.iconClass}`}>
                                                                {area.shortName}
                                                            </span>
                                                            <span className="nursing-name">
                                                                {area.name}
                                                            </span>
                                                        </div>
                                                        <div className="nursing-detail-info">
                                                            <div className="nursing-count">
                                                                {area.count}
                                                            </div>
                                                            <div className="nursing-percent">
                                                                {area.percent}%
                                                            </div>
                                                        </div>
                                                        <div className="nursing-meta">
                                                            总床位: {area.bedData.total}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div
                                                    style={{
                                                        textAlign: 'center',
                                                        color: '#999',
                                                        padding: '20px 0',
                                                    }}>
                                                    暂无护理区数据
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Col>

                                {/* 右侧：床位使用情况 */}
                                <Col xs={24} md={12}>
                                    <div className="bed-usage-section">
                                        <div className="section-title">
                                            {selectedNursingArea
                                                ? `${selectedNursingArea} - 床位使用情况`
                                                : '床位使用情况'}
                                        </div>
                                        <div className="bed-usage-list">
                                            <div className="bed-usage-item">
                                                <span className="bed-label">总床位数</span>
                                                <span className="bed-value">{bedUsage.total}</span>
                                            </div>
                                            <div className="bed-usage-item">
                                                <span className="bed-label">已入住</span>
                                                <span className="bed-value green">
                                                    {bedUsage.occupied}
                                                </span>
                                            </div>
                                            <div className="bed-usage-item">
                                                <span className="bed-label">空闲床位</span>
                                                <span className="bed-value blue">
                                                    {bedUsage.available}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="bed-usage-progress">
                                            <div className="progress-bar">
                                                <div
                                                    className="progress-fill"
                                                    style={{
                                                        width: `${bedUsage.usageRate}%`,
                                                    }}></div>
                                            </div>
                                            <div className="progress-text">
                                                使用率: {bedUsage.usageRate}%
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Spin>
    );
};

export default Dashboard;
